// ── Match Ranking ──────────────────────────────────────────────
// Pure TypeScript — no platform dependencies.

import type { MatchScoreResult, RankedMatch } from './types.ts';

/**
 * Sort criteria (deterministic, stable):
 * 1. Highest Match Score
 * 2. Higher Critical Criteria score (budget + propertyType + rooms)
 * 3. Higher Budget score
 * 4. Higher Location score
 * 5. Stable tie-break (original order)
 */
function compareForRanking<T>(
  a: { score: MatchScoreResult },
  b: { score: MatchScoreResult },
): number {
  const sa = a.score;
  const sb = b.score;

  if (sb.score !== sa.score) return sb.score - sa.score;

  const aCritical = sa.breakdown.budget + sa.breakdown.propertyType + sa.breakdown.rooms;
  const bCritical = sb.breakdown.budget + sb.breakdown.propertyType + sb.breakdown.rooms;
  if (aCritical !== bCritical) return bCritical - aCritical;

  if (sb.breakdown.budget !== sa.breakdown.budget) return sb.breakdown.budget - sa.breakdown.budget;

  if (sb.breakdown.location !== sa.breakdown.location) return sb.breakdown.location - sa.breakdown.location;

  return 0;
}

/**
 * Ranks an array of matched items (already passed through Candidate Gate + Match Score).
 * Returns a new array sorted by ranking criteria, each with rankPosition and totalQualityMatches.
 */
export function rankMatches<T>(
  matches: Array<{ item: T; score: MatchScoreResult }>,
): Array<RankedMatch<T>> {
  const indexed = matches.map((m, idx) => ({ ...m, _idx: idx }));

  indexed.sort((a, b) => {
    const cmp = compareForRanking(a, b);
    return cmp !== 0 ? cmp : a._idx - b._idx;
  });

  const total = indexed.length;
  return indexed.map(({ item, score, _idx }, i) => ({
    item,
    score,
    rankPosition: i + 1,
    totalQualityMatches: total,
  }));
}

// ── Presentation Priority (UI-only, but deterministic) ────────────────────────

export type PresentationPriorityLevel = 'very_high' | 'high' | 'medium' | 'low' | 'very_low';

export interface PresentationPriority {
  stars: number; // 1-5
  level: PresentationPriorityLevel;
  label: string;
}

const PRIORITY_LABELS: Record<PresentationPriorityLevel, string> = {
  very_high: 'גבוהה מאוד',
  high: 'גבוהה',
  medium: 'בינונית',
  low: 'נמוכה',
  very_low: 'נמוכה מאוד',
};

/**
 * Computes a deterministic Presentation Priority (1-5 stars) using only
 * existing Match Score information. No GPT, no new business rules.
 */
export function computePresentationPriority(
  score: MatchScoreResult,
): PresentationPriority {
  const s = score.score;
  const breakdown = score.breakdown;

  const criticalMax = 25 + 10 + 15; // budget + propertyType + rooms = 50
  const criticalScore = breakdown.budget + breakdown.propertyType + breakdown.rooms;
  const criticalRatio = criticalScore / criticalMax; // 0..1

  const budgetRatio = breakdown.budget / 25; // 0..1
  const locationRatio = breakdown.location / 25; // 0..1

  let points = 0;

  if (s >= 90) points += 3;
  else if (s >= 80) points += 2;
  else if (s >= 70) points += 1;

  if (criticalRatio >= 0.9) points += 2;
  else if (criticalRatio >= 0.7) points += 1;

  if (budgetRatio >= 0.85) points += 1;
  if (locationRatio >= 0.8) points += 1;

  let stars: number;
  let level: PresentationPriorityLevel;

  if (points >= 6) { stars = 5; level = 'very_high'; }
  else if (points >= 4) { stars = 4; level = 'high'; }
  else if (points >= 3) { stars = 3; level = 'medium'; }
  else if (points >= 1) { stars = 2; level = 'low'; }
  else { stars = 1; level = 'very_low'; }

  return { stars, level, label: PRIORITY_LABELS[level] };
}

export const MEDAL_EMOJI: Record<number, string> = {
  1: '🥇',
  2: '🥈',
  3: '🥉',
};
